import { BlogPost } from '@/types/blog'
import { Button } from '@/components/ui/button'
import { CheckCircle2, MessageCircle } from 'lucide-react'
import { Link } from 'react-router-dom'

const whatsappUrl =
  'https://wa.me/5511963087549?text=Ol%C3%A1%2C%20li%20o%20artigo%20sobre%20futebol%20para%202%20anos%20e%20gostaria%20de%20saber%20mais%20sobre%20as%20aulas%20do%20SuperFut%20Kids%21'

export const postFutebol2Anos: BlogPost = {
  slug: 'futebol-para-criancas-de-2-anos',
  title: 'Futebol para Crianças de 2 Anos - Pode? É Cedo Demais?',
  excerpt:
    'Descubra se seu filho de 2 anos já pode começar no futebol. Entenda os benefícios, a metodologia adequada e como as aulas ajudam no desenvolvimento motor.',
  date: '16 Fev, 2026',
  author: 'Especialistas SuperFut',
  category: 'Desenvolvimento',
  location: 'São Paulo, SP',
  image:
    'https://img.usecurling.com/p/800/400?q=toddler%20playing%20soccer%20grass',
  metaDescription:
    'Descubra se seu filho de 2 anos pode começar aulas de futebol. Benefícios, metodologia e cuidados para bebês.',
  faq: [
    {
      question: 'Criança de 2 anos entende as regras?',
      answer:
        'Não esperamos que entendam regras complexas. Nesta idade, o foco é lúdico, sensorial e motor, sem cobrança de regras formais do esporte.',
    },
    {
      question: 'É perigoso para essa idade?',
      answer:
        'Com a metodologia correta e supervisão constante, é muito seguro. Utilizamos materiais macios e atividades adequadas ao desenvolvimento da criança.',
    },
    {
      question: 'Qual a duração ideal da aula?',
      answer:
        'Para crianças de 2 anos, recomendamos aulas de 30 a 40 minutos, pois o tempo de concentração é menor.',
    },
    {
      question: 'Precisa usar chuteira?',
      answer:
        'Não é obrigatório. O mais importante é um tênis confortável e antiderrapante para evitar quedas.',
    },
    {
      question: 'Meninas também podem fazer?',
      answer:
        'Com certeza! O futebol é para todos e beneficia meninos e meninas igualmente no desenvolvimento motor e social.',
    },
    {
      question: 'Os pais participam da aula?',
      answer:
        'Sim, na fase de 2 anos, a participação dos pais é incentivada para dar segurança e criar vínculo durante a atividade.',
    },
    {
      question: 'A bola é tamanho oficial?',
      answer:
        'Não. Utilizamos bolas menores e mais leves (tamanho 3 ou de espuma) para não machucar e facilitar o controle.',
    },
    {
      question: 'Ajuda a criança a andar melhor?',
      answer:
        'Sim, o futebol estimula o equilíbrio, a coordenação e o fortalecimento muscular, auxiliando na marcha e corrida.',
    },
    {
      question: 'Pode ser feito no apartamento?',
      answer:
        'Sim! Nossas aulas a domicílio são adaptadas para espaços como salas de estar, play ou varandas.',
    },
    {
      question: 'Como agendar uma aula experimental?',
      answer:
        'Basta clicar no botão de WhatsApp nesta página e falar com nossa equipe para verificar disponibilidade.',
    },
  ],
  content: (
    <div className="space-y-8 text-slate-700 leading-relaxed">
      <div className="prose prose-slate max-w-none">
        <p className="text-xl font-medium text-super-text border-l-4 border-super-green pl-4 italic">
          "Meu filho tem apenas 2 anos, ele já pode fazer aula de futebol?"
        </p>
        <p className="mt-4">
          A resposta curta e direta é: <strong>SIM, pode!</strong> E mais do que
          poder, é altamente recomendado por especialistas em desenvolvimento
          infantil, desde que a atividade seja conduzida com a metodologia
          adequada para essa faixa etária.
        </p>
        <p>
          Muitos pais acreditam que o futebol é um esporte complexo, cheio de
          regras e contato físico, o que seria inadequado para um bebê de 2
          anos. No entanto, quando falamos de iniciação esportiva na primeira
          infância, não estamos falando de um jogo de 11 contra 11 com árbitro e
          impedimento. Estamos falando de{' '}
          <strong>estímulo motor, descoberta corporal e muita diversão</strong>.
        </p>
      </div>

      <section>
        <h2 className="text-2xl md:text-3xl font-display font-bold text-super-text mb-4">
          O que dizem os especialistas?
        </h2>
        <p className="mb-4">
          A <strong>Academia Americana de Pediatria (AAP)</strong> e a Sociedade
          Brasileira de Pediatria recomendam que crianças pequenas tenham
          oportunidades diárias de movimento livre e atividades estruturadas.
          Aos 2 anos, a criança vive uma explosão de desenvolvimento motor. Elas
          estão aprendendo a correr com mais firmeza, pular, e chutar objetos é
          um reflexo natural dessa fase.
        </p>
        <p>
          Introduzir o futebol (ou atividades com bola) neste momento aproveita
          o interesse natural da criança para desenvolver habilidades
          fundamentais, como:
        </p>
        <ul className="grid gap-3 sm:grid-cols-2 mt-4">
          {[
            'Equilíbrio unipodal (ficar num pé só)',
            'Coordenação olho-pé',
            'Noção espacial',
            'Socialização precoce',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-super-green mt-1 flex-shrink-0" />
              <span className="font-medium">{item}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="bg-slate-50 p-8 rounded-2xl border border-slate-100">
        <h2 className="text-2xl md:text-3xl font-display font-bold text-super-text mb-6">
          A Metodologia SuperFut para 2 Anos
        </h2>
        <div className="space-y-6">
          <p>
            No <strong>SuperFut Kids</strong>, entendemos que uma criança de 2
            anos não é um "adulto em miniatura". Nossa abordagem é baseada no
            lúdico. A bola se torna um "tesouro" a ser protegido, os cones viram
            "montanhas" a serem desviadas e o gol é a "casa do gigante".
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-4 rounded-xl shadow-sm">
              <h3 className="font-bold text-lg text-super-blue mb-2">
                Sem Pressão
              </h3>
              <p className="text-sm">
                O objetivo não é formar craques agora, mas sim crianças felizes
                e ativas. Errar faz parte da brincadeira.
              </p>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm">
              <h3 className="font-bold text-lg text-super-green mb-2">
                Materiais Adaptados
              </h3>
              <p className="text-sm">
                Bolas de espuma ou tamanho reduzido, cones coloridos e materiais
                macios garantem a segurança.
              </p>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm">
              <h3 className="font-bold text-lg text-super-yellow mb-2">
                Vínculo Familiar
              </h3>
              <p className="text-sm">
                Nossas aulas podem ser feitas com a participação dos pais,
                fortalecendo a relação através do esporte.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-2xl md:text-3xl font-display font-bold text-super-text mb-4">
          Desenvolvimento Motor aos 2 Anos
        </h2>
        <p className="mb-4">
          Nesta idade, o cérebro da criança está criando milhões de conexões
          neurais por segundo. Atividades que exigem coordenação complexa, como
          correr e chutar uma bola simultaneamente, são "superalimentos" para o
          cérebro infantil.
        </p>
        <p className="mb-4">
          O futebol exige que a criança planeje o movimento (cognitivo), execute
          o movimento (motor) e interaja com o professor ou colega (social). É
          uma atividade completa que trabalha:
        </p>
        <ul className="list-disc pl-6 space-y-2 mb-6">
          <li>
            <strong>Propriocepção:</strong> Saber onde está o seu corpo no
            espaço.
          </li>
          <li>
            <strong>Tônus Muscular:</strong> Fortalecimento das pernas e tronco.
          </li>
          <li>
            <strong>Planejamento Motor:</strong> Calcular a força necessária
            para o chute.
          </li>
        </ul>
      </section>

      <section className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-3xl text-center">
        <h2 className="text-2xl md:text-3xl font-display font-bold text-super-text mb-4">
          Vamos começar essa jornada?
        </h2>
        <p className="text-lg text-slate-700 mb-8 max-w-2xl mx-auto">
          Seu filho tem muita energia e você quer direcioná-la para algo
          positivo? Agende uma aula experimental do SuperFut Kids. Nossos
          professores são especialistas em primeira infância e vão até o seu
          condomínio.
        </p>
        <Button
          size="lg"
          className="bg-[#25D366] hover:bg-[#128C7E] text-white font-bold text-lg px-8 py-6 rounded-full shadow-lg hover:scale-105 transition-all w-full sm:w-auto animate-pulse"
          asChild
        >
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
            <MessageCircle className="w-6 h-6 mr-2" />
            Agendar Aula Experimental
          </a>
        </Button>
      </section>
    </div>
  ),
}
